import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import { CountTabs } from '@/shared/components/CountTabs';
import { ErrorState } from '@/shared/components/ErrorState';
import { ListPagination } from '@/shared/components/ListPagination';
import { PageHeader } from '@/shared/components/PageHeader';
import { useListUrlState } from '@/shared/hooks/useListUrlState';
import { useRoleStore } from '@/shared/session/roleStore';
import type { Draft } from '../api/draftsApi';
import { DraftDetail } from '../components/DraftDetail';
import { DraftList } from '../components/DraftList';
import { DraftSearch } from '../components/DraftSearch';
import { RejectDialog } from '../components/RejectDialog';
import { useDraftDecisions } from '../hooks/useDraftDecisions';
import { useDraftCount, useDraftsQuery, useListsQuery } from '../hooks/useDraftQueries';

const DEFAULTS = { pageSize: 20, filters: { status: 'pending', q: '' } };

const EMPTY: Record<string, string> = {
  pending: 'Nothing waiting for approval. New requests from RMs and Teams appear here.',
  sent: 'Nothing sent yet.',
  rejected: 'Nothing rejected.',
};

const emptyText = (q: string, status: string) => (q ? 'No message matches your search.' : (EMPTY[status] ?? ''));

function StatusTabs({ value, onChange }: { value: string; onChange: (status: string) => void }) {
  const waiting = useDraftCount('pending').data;
  const sent = useDraftCount('sent').data;
  const rejected = useDraftCount('rejected').data;
  return (
    <CountTabs
      fullWidth
      label="Message status"
      value={value}
      onChange={onChange}
      options={[{ value: 'pending', label: 'Waiting', count: waiting }, { value: 'sent', label: 'Sent', count: sent }, { value: 'rejected', label: 'Rejected', count: rejected }]}
    />
  );
}

type DetailProps = { draft: Draft | undefined; decisions: ReturnType<typeof useDraftDecisions> };

function Detail({ draft, decisions }: DetailProps) {
  const lists = useListsQuery().data ?? [];
  const role = useRoleStore((s) => s.role);
  if (!draft) return null;
  return (
    <DraftDetail
      key={draft.id}
      draft={draft}
      lists={lists}
      canDecide={role === 'analyst'}
      role={role}
      isBusy={decisions.isBusy}
      onApprove={(text, listId) => { decisions.approve(draft, text, listId); }}
      onReject={() => { decisions.startReject(draft); }}
    />
  );
}

type QueueProps = {
  list: ReturnType<typeof useListUrlState<typeof DEFAULTS.filters>>;
  status: string;
  query: ReturnType<typeof useDraftsQuery>;
  selected: string;
  onSelect: (id: string) => void;
};

/** The left column: status tabs, search and the messages, newest first. */
function Queue({ list, status, query, selected, onSelect }: QueueProps) {
  const { data } = query;
  const lists = useListsQuery().data ?? [];
  return (
    <Stack sx={{ ...columnSx, minWidth: 0 }}>
      <Stack sx={{ gap: 1, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <StatusTabs value={status} onChange={(v) => { list.setFilters({ status: v }); }} />
        <DraftSearch value={list.filters.q} onChange={(q) => { list.setFilters({ q }); }} />
      </Stack>
      {query.isError ? <Box sx={{ p: 2 }}><ErrorState error={query.error} onRetry={() => void query.refetch()} /></Box> : null}
      {query.isPending ? <Stack sx={{ p: 2, gap: 1.5 }}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={64} />)}</Stack> : null}
      {data?.total === 0 ? <Typography variant="body2" sx={{ color: 'text.secondary', p: 2.5 }}>{emptyText(list.filters.q, status)}</Typography> : null}
      <DraftList drafts={data?.items ?? []} lists={lists} selected={selected} onSelect={onSelect} />
      {data && data.total > list.pageSize ? (
        <ListPagination total={data.total} page={list.page} pageSize={list.pageSize} onPageChange={list.setPage} onPageSizeChange={list.setPageSize} />
      ) : null}
    </Stack>
  );
}

/** Every outgoing WhatsApp message, one at a time: the queue on the left, the selected message on the right. */
export function ApprovalsPage() {
  const list = useListUrlState(DEFAULTS);
  const status = list.filters.status || 'pending';
  const query = useDraftsQuery({ ...list.params, status });
  const decisions = useDraftDecisions();
  const [searchParams, setSearchParams] = useSearchParams();
  const items = query.data?.items ?? [];
  const selected = items.find((d) => d.id === searchParams.get('draft')) ?? items[0];
  const select = (id: string) => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('draft', id); return next; }, { replace: true });
  };

  return (
    <>
      <PageHeader title="Approvals" description="Every outgoing WhatsApp message waits here until a research analyst approves it." />
      <Box sx={frameSx}>
        <Queue list={list} status={status} query={query} selected={selected?.id ?? ''} onSelect={select} />
        {selected ? <Box sx={{ ...columnSx, minWidth: 0 }}><Detail draft={selected} decisions={decisions} /></Box> : null}
      </Box>
      {decisions.rejecting ? (
        <RejectDialog
          requester={decisions.rejecting.requester.name}
          channel={decisions.rejecting.channel}
          isPending={decisions.isRejectPending}
          onCancel={decisions.cancelReject}
          onReject={decisions.reject}
        />
      ) : null}
      <Snackbar open={decisions.toast !== null} autoHideDuration={4000} onClose={decisions.clearToast} message={decisions.toast} />
    </>
  );
}

const frameSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 360px) minmax(0, 1fr)' },
  gap: 2,
  alignItems: 'start',
} as const;

const columnSx = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  overflow: 'hidden',
} as const;
